import { useSyncExternalStore } from 'react';
import type { SocialProfile } from '@teaplus/shared';

/**
 * Nicknames and pictures you give your friends. They stay on this PC (per profile), are never
 * sent to Tea+'s server, and your friends never see them.
 */
export interface FriendLook {
  nickname?: string;
  /** A small square picture, as a data URL. */
  image?: string;
}

type Looks = Record<string, FriendLook>;

let ownerId: string | null = null;
let looks: Looks = {};
let version = 0;
const listeners = new Set<() => void>();

const keyFor = (profileId: string) => `tp:friend-looks:${profileId}`;

/** Loads the looks for the profile in use; called whenever the active profile changes. */
export function useFriendLooksFor(profileId: string | null | undefined) {
  if ((profileId ?? null) !== ownerId) {
    ownerId = profileId ?? null;
    try {
      looks = ownerId ? (JSON.parse(localStorage.getItem(keyFor(ownerId)) ?? '{}') as Looks) : {};
    } catch {
      looks = {};
    }
    version++;
  }
}

function save() {
  version++;
  if (ownerId)
    try {
      localStorage.setItem(keyFor(ownerId), JSON.stringify(looks));
    } catch {}
  listeners.forEach((l) => l());
}

export function setFriendLook(friendId: string, look: FriendLook) {
  const clean: FriendLook = {};
  if (look.nickname?.trim()) clean.nickname = look.nickname.trim().slice(0, 40);
  if (look.image) clean.image = look.image;
  if (clean.nickname || clean.image) looks = { ...looks, [friendId]: clean };
  else {
    const { [friendId]: _gone, ...rest } = looks;
    looks = rest;
  }
  save();
}

export const friendLook = (friendId: string): FriendLook => looks[friendId] ?? {};

/** Changes whenever a nickname or picture changes, so lists re-apply them. */
export function useFriendLooksVersion() {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => version,
  );
}

/** A friend as you see them: your nickname and picture over theirs, with their real name kept. */
export function looked<T extends SocialProfile>(
  p: T,
): T & { realName: string; realAvatarUrl: string | null } {
  const l = looks[p.id];
  const before = p as { realName?: string; realAvatarUrl?: string | null };
  return {
    ...p,
    realName: before.realName ?? p.name,
    realAvatarUrl: before.realAvatarUrl !== undefined ? before.realAvatarUrl : p.avatarUrl,
    name: l?.nickname || p.name,
    avatarUrl: l?.image || p.avatarUrl,
  };
}

/** Shrinks a picked image to a 128px square (centre-cropped) WebP data URL. */
export async function pictureFromFile(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    const side = Math.min(img.naturalWidth, img.naturalHeight);
    const c = document.createElement('canvas');
    c.width = c.height = 128;
    c.getContext('2d')!.drawImage(
      img,
      (img.naturalWidth - side) / 2,
      (img.naturalHeight - side) / 2,
      side,
      side,
      0,
      0,
      128,
      128,
    );
    return c.toDataURL('image/webp', 0.85);
  } finally {
    URL.revokeObjectURL(url);
  }
}
